/* eslint-disable no-shadow */
/* eslint-disable @typescript-eslint/no-shadow */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Monitor,
  Circle,
  AudioLines,
  VideoOff,
  MicOff,
  Mic,
  Video,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
  updateSelectedDevices,
  setDisplaySurface,
  setMicrophoneAudio,
} from '../../recorderSlice';
import type { DisplaySurface } from '../../recorderSlice';
import {
  fetchAvailableDevices,
  fetchDisplayMediaThunk,
  fetchUserMediaThunk,
  initializeUploadThunk,
  startRecordingThunk,
  createBackupRecordThunk,
  cancelThunk,
} from '../../thunk';
import type { RecorderState, AppDispatch } from '../../store';
import logger from '../../../lib/logger';
import trackRecorderError from '../../utils/errorTracking';
import MacAppPromotionBanner from '../../../components/common/MacAppPromotionBanner';
import { streams } from '../utils/streamUtils';
import MicIconWithAudioVisualizer from './MicIconWithAudioVisualizer';

const ENABLE_EXPERIMENTAL_SPLIT_START_CTA = false;

const DeviceSelection = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const isSmallViewport =
    typeof window !== 'undefined' && window.innerWidth < 640;

  const truncateText = (text: string, maxLength: number = 30) => {
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };
  const {
    status: recorderStatus,
    selectedDevices,
    availableDevices,
    mediaStreams,
    settings,
    upload,
  } = useSelector((state: RecorderState) => state.recorder);
  const { status: userMediaStatus } = mediaStreams.userMedia;
  const { videoinput, audioinput, displaySurface } = selectedDevices;
  const { webCam, screen } = settings?.videoSettings || {};
  const { microphoneAudio } = settings?.audioSettings || {};
  const isScreenModeSelected = !!screen && displaySurface !== 'none';
  const isExperimentalSplitStartMode =
    ENABLE_EXPERIMENTAL_SPLIT_START_CTA && !!screen;
  const [isWithScreenshareButtonEngaged, setIsWithScreenshareButtonEngaged] =
    useState(false);
  const [showAudioWarning, setShowAudioWarning] = useState(false);
  const [showNoAudioAlert, setShowNoAudioAlert] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [noSystemAudioWarning, setNoSystemAudioWarning] = useState(false);
  const originalTitle = useRef(document.title);
  const showWarningAlert = showAudioWarning && screen;
  const showErrorAlert = showNoAudioAlert && audioinput.deviceId !== 'none';

  // Helper to check if upload is already initialized
  const isUploadInitialized = () => {
    return upload.uploadId && upload.fileId;
  };

  useEffect(() => {
    const handleDeviceChange = () => {
      console.log('Device change detected');
      dispatch(
        fetchAvailableDevices({ camera: webCam, microphone: microphoneAudio }),
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        dispatch(
          fetchAvailableDevices({
            camera: webCam,
            microphone: microphoneAudio,
          }),
        );
      }
    };

    // Initial fetch
    dispatch(
      fetchAvailableDevices({ camera: webCam, microphone: microphoneAudio }),
    );

    // Setup event listeners
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange,
      );
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch]);

  useEffect(() => {
    setShowAudioWarning(isScreenModeSelected);
  }, [isScreenModeSelected]);

  // Load saved audio settings from localStorage
  useEffect(() => {
    // Don't restore saved mic device when microphoneAudio is explicitly disabled
    if (!microphoneAudio) return;

    const savedSettings = localStorage.getItem('recorderSettings');
    if (savedSettings) {
      const parsedSettings = JSON.parse(savedSettings);
      const savedAudioInput = parsedSettings?.selectedDevices?.audioinput;

      if (savedAudioInput !== 'none') {
        // Check if the saved device still exists in available devices
        const deviceExists = availableDevices.audioinput.find(
          (device) => device.deviceId === savedAudioInput,
        );

        if (deviceExists) {
          // Use the saved device if it exists
          dispatch(updateSelectedDevices({ audioinput: savedAudioInput }));
        } else if (availableDevices.audioinput.length > 0) {
          // If saved device not found, use the first available device
          dispatch(
            updateSelectedDevices({
              audioinput: availableDevices.audioinput[0].deviceId,
            }),
          );
        } else {
          // If no devices available, set to none
          dispatch(updateSelectedDevices({ audioinput: 'none' }));
        }
      } else {
        dispatch(updateSelectedDevices({ audioinput: 'none' }));
      }
    }
  }, [availableDevices, dispatch, microphoneAudio]);

  // Helper function to update localStorage
  const updateRecorderSettings = (
    kind: 'audio' | 'video',
    deviceId: string,
  ) => {
    const currentSettings = JSON.parse(
      localStorage.getItem('recorderSettings') || '{}',
    );
    const updatedSettings = {
      ...currentSettings,
      selectedDevices: {
        ...currentSettings.selectedDevices,
        [kind === 'audio' ? 'audioinput' : 'videoinput']: deviceId,
      },
    };
    localStorage.setItem('recorderSettings', JSON.stringify(updatedSettings));
  };

  const getHandleDeviceChange =
    (kind: 'audio' | 'video') => async (deviceId: string) => {
      try {
        const result = await (dispatch(
          fetchUserMediaThunk({
            [kind]: deviceId === 'none' ? false : { deviceId },
          }),
        ) as unknown as Promise<{
          streamId: string;
          trackInfo: Pick<MediaStreamTrack, 'label' | 'id' | 'kind'>[];
        }>);

        if (kind === 'audio') {
          if (deviceId === 'none') {
            setShowNoAudioAlert(false);
          } else {
            const stream = result as unknown as {
              payload: {
                trackInfo: Pick<MediaStreamTrack, 'label' | 'id' | 'kind'>[];
              };
            };
            // Just check if there's any audio track, not the specific label
            const hasAudioTrack = stream?.payload?.trackInfo?.some(
              (track) => track.kind === 'audio',
            );
            setShowNoAudioAlert(!hasAudioTrack);
          }
        } else {
          setShowNoAudioAlert(false);
        }
        updateRecorderSettings(kind, deviceId);
      } catch (error) {
        console.error('Error changing device:', error);
        if (kind === 'audio' && deviceId !== 'none') {
          setShowNoAudioAlert(true);
        }
      }
    };

  // Helper function to update localStorage
  const onChangeDisplaySurface = (value: DisplaySurface) => {
    dispatch(setDisplaySurface(value));
    const currentSettings = JSON.parse(
      localStorage.getItem('recorderSettings') || '{}',
    );
    const updatedSettings = {
      ...currentSettings,
      selectedDevices: {
        ...currentSettings.selectedDevices,
        displaySurface: value,
      },
    };
    localStorage.setItem('recorderSettings', JSON.stringify(updatedSettings));
  };

  const handleStart = async (
    startMode: 'selected' | 'with-screen' | 'without-screen' = 'selected',
  ) => {
    // Prevent multiple initialization attempts
    if (hasInitialized || isUploadInitialized() || recorderStatus !== 'ready') {
      return;
    }

    let shouldStartWithScreenshare = isScreenModeSelected;
    if (startMode === 'with-screen') {
      shouldStartWithScreenshare = !!screen;
    } else if (startMode === 'without-screen') {
      shouldStartWithScreenshare = false;
    }
    const effectiveDisplaySurface = shouldStartWithScreenshare
      ? 'browser'
      : 'none';

    if (startMode === 'with-screen') {
      onChangeDisplaySurface('browser');
    } else if (startMode === 'without-screen') {
      onChangeDisplaySurface('none');
    }

    // Acquire user media streams (camera/mic) right before recording
    await dispatch(
      fetchUserMediaThunk({
        video:
          videoinput.deviceId !== 'none'
            ? { deviceId: videoinput.deviceId }
            : false,
        audio:
          audioinput.deviceId !== 'none'
            ? { deviceId: audioinput.deviceId }
            : false,
      }),
    );

    if (shouldStartWithScreenshare) {
      const fetchDisplayMediaResult = await dispatch(
        fetchDisplayMediaThunk({
          video: true,
          audio: true,
        }),
      );
      if (
        // @ts-ignore - TS doesn't know about the type of the action
        fetchDisplayMediaResult.type === fetchDisplayMediaThunk.rejected.type
      ) {
        logger.log('Error fetching display media');
        dispatch(cancelThunk());
        return;
      }

      // Check if the user actually shared system audio
      const displayTrackInfo = (fetchDisplayMediaResult as any)?.payload
        ?.trackInfo;
      const hasDisplayAudio = displayTrackInfo?.some(
        (track: { kind: string }) => track.kind === 'audio',
      );
      if (!hasDisplayAudio) {
        setNoSystemAudioWarning(true);
        return;
      }
    }
    if (
      !(
        effectiveDisplaySurface === 'none' &&
        videoinput.deviceId === 'none' &&
        audioinput.deviceId === 'none'
      )
    ) {
      setHasInitialized(true);
      const initResult = await dispatch(initializeUploadThunk());
      if (
        // @ts-ignore - TS doesn't know about the type of the action
        initResult.type === initializeUploadThunk.rejected.type
      ) {
        logger.error('Upload initialization failed, not starting recording');
        setHasInitialized(false); // Reset flag on failure
        trackRecorderError({
          operation: 'initialization',
          errorType: 'InitializationError',
          errorMessage: 'Upload initialization failed in device selection',
          additionalContext: {
            source: 'device_selection',
            displaySurface: effectiveDisplaySurface,
            selectedVideoinput: videoinput.deviceId,
            selectedAudioinput: audioinput.deviceId,
          },
        });
        return;
      }

      await dispatch(startRecordingThunk());
      await dispatch(createBackupRecordThunk());
    }
  };

  // Add new useEffect for auto-starting when isTabAudioOnly is true
  useEffect(() => {
    if (
      settings?.audioSettings?.isTabAudioOnly &&
      recorderStatus === 'ready' &&
      availableDevices.status === 'succeeded' &&
      !hasInitialized &&
      !isUploadInitialized() &&
      !noSystemAudioWarning
    ) {
      handleStart();
    }
  }, [
    settings?.audioSettings?.isTabAudioOnly,
    recorderStatus,
    availableDevices.status,
    hasInitialized,
    noSystemAudioWarning,
  ]);

  /**
   * "Continue with Microphone Only" — enable mic as fallback, proceed without system audio.
   */
  const handleShareAnyway = async () => {
    setHasInitialized(true);

    const firstMic = availableDevices.audioinput[0];
    if (firstMic) {
      dispatch(updateSelectedDevices({ audioinput: firstMic.deviceId }));
      dispatch(setMicrophoneAudio(true));
      await dispatch(
        fetchUserMediaThunk({ audio: { deviceId: firstMic.deviceId } }),
      );
    }

    const initResult = await dispatch(initializeUploadThunk());
    if (
      // @ts-ignore
      initResult.type === initializeUploadThunk.rejected.type
    ) {
      setHasInitialized(false);
      return;
    }
    await dispatch(startRecordingThunk());
    await dispatch(createBackupRecordThunk());
  };

  /**
   * "Share system audio" — stop current display stream, re-trigger Chrome's share dialog.
   */
  const handleReShareSystemAudio = async () => {
    setHasInitialized(true);
    setNoSystemAudioWarning(false);

    // Stop existing display media tracks
    streams.displayMediaStream.getTracks().forEach((track) => {
      track.stop();
      streams.displayMediaStream.removeTrack(track);
    });

    const fetchDisplayMediaResult = await dispatch(
      fetchDisplayMediaThunk({ video: true, audio: true }),
    );
    if (
      // @ts-ignore
      fetchDisplayMediaResult.type === fetchDisplayMediaThunk.rejected.type
    ) {
      dispatch(cancelThunk());
      return;
    }

    // Check again
    const displayTrackInfo = (fetchDisplayMediaResult as any)?.payload
      ?.trackInfo;
    const hasDisplayAudio = displayTrackInfo?.some(
      (track: { kind: string }) => track.kind === 'audio',
    );
    if (!hasDisplayAudio) {
      setNoSystemAudioWarning(true);
      setHasInitialized(false);
      return;
    }

    // Audio present — proceed
    const initResult = await dispatch(initializeUploadThunk());
    if (
      // @ts-ignore
      initResult.type === initializeUploadThunk.rejected.type
    ) {
      setHasInitialized(false);
      return;
    }
    await dispatch(startRecordingThunk());
    await dispatch(createBackupRecordThunk());
  };

  // Reset initialization flag when recorder status changes back to initializing
  useEffect(() => {
    if (recorderStatus === 'initializing') {
      setHasInitialized(false);
    }
  }, [recorderStatus]);

  /**
   * Flash the document title when the system audio warning is shown,
   * since the shared tab gets focused and the user may not notice.
   */
  const titleFlashInterval = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  useEffect(() => {
    if (!noSystemAudioWarning) {
      if (titleFlashInterval.current) {
        clearInterval(titleFlashInterval.current);
        titleFlashInterval.current = null;
        document.title = originalTitle.current;
      }
      return undefined;
    }

    const warningText = t(
      'deviceSelection.noSystemAudio.titleFlash',
      '⚠ No system audio',
    );
    originalTitle.current = document.title;
    let showWarning = true;

    titleFlashInterval.current = setInterval(() => {
      document.title = showWarning ? warningText : originalTitle.current;
      showWarning = !showWarning;
    }, 1000);

    return () => {
      if (titleFlashInterval.current) {
        clearInterval(titleFlashInterval.current);
        titleFlashInterval.current = null;
      }
      document.title = originalTitle.current;
    };
  }, [noSystemAudioWarning, t]);

  if (recorderStatus !== 'ready') {
    return null;
  }

  if (
    availableDevices.status === 'pending' &&
    availableDevices.videoinput.length === 0 &&
    availableDevices.audioinput.length === 0
  ) {
    return <div className="text-center p-4">Loading devices...</div>;
  }

  if (availableDevices.status === 'failed') {
    return (
      <div className="text-center p-4 text-destructive">
        Error loading devices: {availableDevices.error}
      </div>
    );
  }

  if (settings?.audioSettings?.isTabAudioOnly && !noSystemAudioWarning) {
    return null;
  }

  if (noSystemAudioWarning) {
    return (
      <Dialog open onOpenChange={() => dispatch(cancelThunk())}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center text-base">
              {t(
                'deviceSelection.noSystemAudio.title',
                'No system audio detected',
              )}
            </DialogTitle>
            <DialogDescription className="text-center text-sm text-muted-foreground">
              {t(
                'deviceSelection.noSystemAudio.description',
                'System audio is not shared. Only microphone audio will be recorded.',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            <Button onClick={handleReShareSystemAudio} className="w-full">
              {t('deviceSelection.noSystemAudio.reshare', 'Share system audio')}
            </Button>
            <Button
              variant="outline"
              onClick={handleShareAnyway}
              className="w-full"
            >
              {t('deviceSelection.noSystemAudio.shareAnyway', 'Share anyway')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={() => dispatch(cancelThunk())}>
      <DialogContent
        className={cn('max-w-md', isSmallViewport && 'w-full m-2')}
      >
        <DialogHeader>
          <DialogTitle className="text-center text-base">
            {t('deviceSelection.title', 'Choose what to record')}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="min-h-0">
            <MacAppPromotionBanner variant="recorder" />
          </div>

          {screen && !isExperimentalSplitStartMode && (
            <div className="mb-2 grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="record-mode-screen"
                onClick={() => onChangeDisplaySurface('browser')}
                className={cn(
                  'flex h-28 w-full flex-col items-center justify-center gap-2 rounded-lg border bg-background p-3 transition-colors',
                  isScreenModeSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-input hover:border-primary/40',
                )}
              >
                <Monitor className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t('deviceSelection.recordMode.screen', 'Screen')}
                </span>
              </button>
              <button
                type="button"
                data-testid="record-mode-no-screen"
                onClick={() => onChangeDisplaySurface('none')}
                className={cn(
                  'flex h-28 w-full flex-col items-center justify-center gap-2 rounded-lg border bg-background p-3 transition-colors',
                  !isScreenModeSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-input hover:border-primary/40',
                )}
              >
                <AudioLines className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t('deviceSelection.recordMode.noScreen', 'Audio only')}
                </span>
              </button>
            </div>
          )}

          {/* Compact device selectors */}
          <div className="flex flex-col gap-2">
            {/* Microphone Selector */}
            {microphoneAudio && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-muted-foreground min-w-[70px]">
                  {t('deviceSelection.microphoneLabel', 'Mic')}:
                </span>
                <Select
                  value={audioinput.deviceId || 'none'}
                  onValueChange={(value) => {
                    getHandleDeviceChange('audio')(value);
                    dispatch(updateSelectedDevices({ audioinput: value }));
                  }}
                >
                  <SelectTrigger className="w-full h-9 border bg-background px-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      {audioinput.deviceId === 'none' ||
                      !audioinput.deviceId ? (
                        <>
                          <MicOff className="h-5 w-5 text-destructive flex-shrink-0" />
                          <span className="text-sm text-destructive truncate">
                            {t('deviceSelection.microphone.off', 'Off')}
                          </span>
                        </>
                      ) : (
                        <>
                          <MicIconWithAudioVisualizer
                            deviceId={audioinput.deviceId}
                            className="h-5 w-5"
                          />
                          <span className="text-sm -ml-2 truncate">
                            {truncateText(
                              availableDevices.audioinput.find(
                                (d) => d.deviceId === audioinput.deviceId,
                              )?.label ||
                                t(
                                  'deviceSelection.microphone.default',
                                  'Microphone',
                                ),
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <div className="flex items-center gap-2 min-w-0">
                        <MicOff className="h-5 w-5 text-destructive flex-shrink-0" />
                        <span className="text-destructive truncate">
                          {t(
                            'deviceSelection.microphone.dontUse',
                            "Don't use microphone",
                          )}
                        </span>
                      </div>
                    </SelectItem>
                    {availableDevices.audioinput.map((device) => (
                      <SelectItem key={device.deviceId} value={device.deviceId}>
                        <div className="flex items-center gap-2 min-w-0">
                          <Mic className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                          <span className="truncate">
                            {truncateText(
                              device.label || `Microphone ${device.deviceId}`,
                            )}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Camera Selector */}
            {webCam && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-muted-foreground min-w-[70px]">
                  {t('deviceSelection.cameraLabel', 'Camera')}:
                </span>
                <Select
                  value={videoinput.deviceId || 'none'}
                  onValueChange={(value) => {
                    getHandleDeviceChange('video')(value);
                    dispatch(updateSelectedDevices({ videoinput: value }));
                  }}
                >
                  <SelectTrigger className="w-full h-9 border bg-background px-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      {videoinput.deviceId === 'none' ||
                      !videoinput.deviceId ? (
                        <>
                          <VideoOff className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm truncate">
                            {t('deviceSelection.camera.off', 'Off')}
                          </span>
                        </>
                      ) : (
                        <>
                          <Video className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm truncate">
                            {truncateText(
                              availableDevices.videoinput.find(
                                (d) => d.deviceId === videoinput.deviceId,
                              )?.label ||
                                t('deviceSelection.camera.default', 'Camera'),
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <div className="flex items-center gap-2 min-w-0">
                        <VideoOff className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        <span className="truncate">
                          {t(
                            'deviceSelection.camera.dontUse',
                            "Don't use camera",
                          )}
                        </span>
                      </div>
                    </SelectItem>
                    {availableDevices.videoinput.map((device) => (
                      <SelectItem key={device.deviceId} value={device.deviceId}>
                        <div className="flex items-center gap-2 min-w-0">
                          <Video className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                          <span className="truncate">
                            {truncateText(
                              device.label || `Camera ${device.deviceId}`,
                            )}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Alerts with smooth animation */}
            <div className="min-h-0">
              {/* Warning Alert */}
              <div
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{
                  maxHeight: showWarningAlert ? '80px' : '0px',
                  opacity: showWarningAlert ? 1 : 0,
                  marginBottom:
                    showWarningAlert && showErrorAlert ? '8px' : '0px',
                }}
              >
                <Alert className="bg-muted border-muted">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <AlertDescription className="text-muted-foreground text-sm">
                      {t(
                        'deviceSelection.audioWarning',
                        'Make sure you enable tab or system audio in the screen-share dialog to capture shared audio.',
                      )}
                    </AlertDescription>
                  </div>
                </Alert>
              </div>

              {/* Error Alert */}
              <div
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{
                  maxHeight: showErrorAlert ? '80px' : '0px',
                  opacity: showErrorAlert ? 1 : 0,
                  marginBottom: '0px',
                }}
              >
                <Alert variant="destructive">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <AlertDescription className="text-sm">
                      {t(
                        'deviceSelection.noAudioAlert',
                        'No audio microphone audio detected. Please select "Don\'t use microphone Audio" if you want to proceed.',
                      )}
                    </AlertDescription>
                  </div>
                </Alert>
              </div>
            </div>
          </div>

          {isExperimentalSplitStartMode ? (
            <div className="mt-2 relative">
              <div className="relative h-10 overflow-hidden rounded-full border border-input bg-background">
                <button
                  id="start-recording-no-screen-button"
                  type="button"
                  onClick={() => handleStart('without-screen')}
                  disabled={
                    userMediaStatus === 'pending' ||
                    (videoinput.deviceId === 'none' &&
                      audioinput.deviceId === 'none')
                  }
                  className={cn(
                    'absolute inset-y-0 left-0 z-10 w-[52%] pl-3 pr-5 text-sm font-medium bg-background text-foreground hover:bg-primary hover:text-primary-foreground focus-visible:bg-primary focus-visible:text-primary-foreground hover:shadow-md transition-[background-color,color,padding,box-shadow] duration-200 ease-out inline-flex items-center justify-end gap-2 disabled:opacity-50 disabled:pointer-events-none outline-none',
                    isWithScreenshareButtonEngaged &&
                      'bg-primary text-primary-foreground shadow-md pr-2.5',
                  )}
                >
                  <Circle className="h-2.5 w-2.5 fill-current" />
                  <span>
                    {t('deviceSelection.startRecording', 'Start recording')}
                  </span>
                </button>
                <button
                  id="start-recording-with-screen-button"
                  type="button"
                  onClick={() => handleStart('with-screen')}
                  onMouseEnter={() => setIsWithScreenshareButtonEngaged(true)}
                  onMouseLeave={() => setIsWithScreenshareButtonEngaged(false)}
                  onFocus={() => setIsWithScreenshareButtonEngaged(true)}
                  onBlur={() => setIsWithScreenshareButtonEngaged(false)}
                  disabled={userMediaStatus === 'pending'}
                  className={cn(
                    'absolute inset-y-0 right-0 z-20 w-[52%] pl-5 pr-3 text-sm font-medium bg-background text-foreground hover:bg-primary hover:text-primary-foreground focus-visible:bg-primary focus-visible:text-primary-foreground hover:shadow-md transition-[background-color,color,padding,box-shadow] duration-200 ease-out inline-flex items-center justify-start gap-2 disabled:opacity-50 disabled:pointer-events-none outline-none',
                    isWithScreenshareButtonEngaged && 'pl-2.5',
                  )}
                  style={{
                    clipPath: 'polygon(7% 0, 100% 0, 100% 100%, 0 100%)',
                  }}
                >
                  <span>
                    {t('deviceSelection.withScreenshare', 'With Screenshare')}
                  </span>
                  <Monitor className="h-4 w-4" />
                </button>
                <div
                  aria-hidden
                  className={cn(
                    'pointer-events-none absolute left-1/2 top-1/2 z-30 h-[165%] w-[2px] -translate-x-1/2 -translate-y-1/2 rotate-[18deg] bg-border/80 transition-opacity duration-200 ease-out',
                    isWithScreenshareButtonEngaged
                      ? 'opacity-0'
                      : 'opacity-100',
                  )}
                />
              </div>
            </div>
          ) : (
            <Button
              id="start-recording-button"
              onClick={() => handleStart('selected')}
              disabled={
                userMediaStatus === 'pending' ||
                (displaySurface === 'none' &&
                  videoinput.deviceId === 'none' &&
                  audioinput.deviceId === 'none')
              }
              size="default"
              className="mt-2 w-full h-9 text-sm"
            >
              {t('deviceSelection.startRecording', 'Start Recording')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeviceSelection;
